import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Loader2, Sparkles } from 'lucide-react';
import { fetchDealBySlug, PublicDeal } from '../api/deals';
import {
  CountdownTimer,
  DealProductCard,
  DealsBreadcrumb,
  SectionHeader,
  dealBannerUrl,
  dealProductImage,
} from '../components/deals/DealsUi';

const DealDetailPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [deal, setDeal] = useState<PublicDeal | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!slug) return;
    fetchDealBySlug(slug)
      .then(setDeal)
      .catch((e) => setError(e.message || 'Deal not found'))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 pt-[72px] sm:pt-20 flex flex-col items-center justify-center gap-4">
        <Loader2 className="w-10 h-10 animate-spin text-orange-500" />
        <p className="text-sm text-slate-500">Loading deal…</p>
      </div>
    );
  }

  if (error || !deal) {
    return (
      <div className="min-h-screen bg-slate-50 pt-[72px] sm:pt-20 px-4">
        <div className="max-w-lg mx-auto text-center py-20">
          <p className="text-red-600 font-medium mb-4">{error || 'Deal not found'}</p>
          <Link
            to="/deals"
            className="inline-flex items-center gap-2 text-orange-600 font-semibold hover:underline"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to all deals
          </Link>
        </div>
      </div>
    );
  }

  const sections =
    deal.sections && deal.sections.length > 0
      ? [...deal.sections].sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
      : [
          {
            title: 'Featured products',
            order: 0,
            layout: 'grid',
            defaultDiscountPercent: 0,
            groups: [],
            products: (deal.products || []).filter((p) => p.product),
          },
        ];

  const banner = dealBannerUrl(deal.bannerImage || deal.bannerMobileImage);
  const totalProducts = sections.reduce((n, s) => n + (s.products?.length || 0), 0);

  return (
    <div className="min-h-screen bg-slate-50 pb-16">
      {/* Hero */}
      <div className="relative pt-[72px] sm:pt-20">
        <div
          className={`relative min-h-[220px] sm:min-h-[280px] md:min-h-[320px] ${
            banner ? '' : 'bg-gradient-to-br from-slate-900 via-red-900 to-orange-600'
          }`}
        >
          {banner && (
            <>
              <img
                src={banner}
                alt=""
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-slate-900/90 via-slate-900/70 to-slate-900/40" />
            </>
          )}
          {!banner && (
            <div className="absolute inset-0 opacity-25 bg-[radial-gradient(circle_at_70%_30%,white,transparent_45%)]" />
          )}

          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
            <DealsBreadcrumb current={deal.title} />

            <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 lg:gap-10">
              <div className="max-w-2xl text-white">
                {deal.displayLabel && (
                  <span className="inline-flex items-center gap-1.5 bg-orange-500 text-white text-xs font-bold uppercase tracking-wide px-3 py-1 rounded-full mb-4">
                    <Sparkles className="w-3.5 h-3.5" />
                    {deal.displayLabel}
                  </span>
                )}
                <h1 className="text-3xl sm:text-4xl md:text-[2.75rem] font-bold tracking-tight leading-tight">
                  {deal.title}
                </h1>
                {deal.subtitle && (
                  <p className="mt-3 text-base sm:text-lg text-white/90 leading-relaxed">
                    {deal.subtitle}
                  </p>
                )}
                {deal.description && (
                  <p className="mt-2 text-sm sm:text-base text-white/75 leading-relaxed line-clamp-3 sm:line-clamp-none">
                    {deal.description}
                  </p>
                )}
                <p className="mt-4 text-sm text-white/70">
                  {totalProducts} products in this offer
                </p>
              </div>

              {deal.showTimer !== false && (
                <div className="flex-shrink-0 bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl px-5 py-4 sm:px-6 sm:py-5">
                  <p className="text-xs font-semibold uppercase tracking-wider text-white/80 mb-3 text-center sm:text-left">
                    Offer ends in
                  </p>
                  <CountdownTimer endDate={deal.endDate} />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Products */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-2 sm:mt-0 pt-8 sm:pt-10">
        <Link
          to="/deals"
          className="inline-flex items-center gap-2 text-sm font-medium text-slate-600 hover:text-orange-600 mb-8 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          All deals
        </Link>

        {sections.map((section, idx) => {
          const items = (section.products || []).filter((row) => row.product?.slug);
          if (!items.length) return null;

          const isSlider = section.layout === 'slider';

          return (
            <section key={section._id || idx} className="mb-12 sm:mb-14 last:mb-0">
              <SectionHeader
                title={section.title}
                count={items.length}
                layout={section.layout || 'grid'}
              />

              {isSlider ? (
                <div className="flex gap-4 overflow-x-auto pb-4 snap-x snap-mandatory scrollbar-thin -mx-4 px-4 sm:mx-0 sm:px-0">
                  {items.map((row, i) => {
                    const p = row.product!;
                    return (
                      <DealProductCard
                        key={p._id || i}
                        slug={p.slug}
                        name={p.name}
                        image={dealProductImage(p)}
                        dealPrice={row.dealPrice}
                        originalPrice={row.originalPrice}
                        discountPercent={row.discountPercent}
                        layout="slider"
                      />
                    );
                  })}
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 sm:gap-5">
                  {items.map((row, i) => {
                    const p = row.product!;
                    return (
                      <DealProductCard
                        key={p._id || i}
                        slug={p.slug}
                        name={p.name}
                        image={dealProductImage(p)}
                        dealPrice={row.dealPrice}
                        originalPrice={row.originalPrice}
                        discountPercent={row.discountPercent}
                        layout="grid"
                      />
                    );
                  })}
                </div>
              )}
            </section>
          );
        })}

        {totalProducts === 0 && (
          <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center text-slate-500">
            No products are listed in this deal yet.
          </div>
        )}
      </div>
    </div>
  );
};

export default DealDetailPage;
